import { api } from "./axiosClient";
import type {
  AiOperationDto,
  AuditLogDto,
  AuditSearchMessage,
  BillingSummaryDto,
  CatalogueResponse,
  DailyUsagePoint,
  EntitlementCheckResult,
  EntitlementSnapshotResponse,
  FeatureDto,
  InvoiceDto,
  LedgerRowDto,
  LicensingSettingsDto,
  MeterDto,
  ModuleDto,
  MySubscriptionResponse,
  OrganisationLicenceDetailResponse,
  OrganisationLicenceRowDto,
  PaymentDto,
  PlanDto,
  PlanEntitlementDto,
  PlanFeatureDto,
  PlanModuleDto,
  RechargeDto,
  RechargePackDto,
  SubscriptionOverrideDto,
} from "../types/licensing.types";

interface Paged<T> {
  count: number;
  data: T[];
}

/**
 * Every call is a POST to /Licensing/<area>/<verb>, matching the backend's
 * controller and the gateway's route table. Super Admin only routes are
 * grouped first; "my/*" is for an organisation's own admin; the two
 * entitlement routes are open to any signed-in user.
 */
export const licensingApi = {
  // ---- catalogue (Super Admin) ----
  getCatalogue: async (includeInactive = true): Promise<CatalogueResponse> =>
    (await api.post("/Licensing/catalogue/get", { includeInactive })).data,
  saveMeter: async (meter: MeterDto): Promise<string> =>
    (await api.post("/Licensing/catalogue/meters/save", meter)).data,
  saveFeature: async (feature: FeatureDto): Promise<string> =>
    (await api.post("/Licensing/catalogue/features/save", feature)).data,
  saveModule: async (module: ModuleDto): Promise<string> =>
    (await api.post("/Licensing/catalogue/modules/save", module)).data,
  saveAiOperation: async (op: AiOperationDto): Promise<string> =>
    (await api.post("/Licensing/catalogue/aioperations/save", op)).data,
  removeCatalogueItem: async (
    kind: "meter" | "feature" | "module" | "aioperation",
    id: string,
  ) => api.post("/Licensing/catalogue/remove", { kind, id }),

  searchPacks: async (includeInactive = true): Promise<RechargePackDto[]> =>
    (await api.post("/Licensing/packs/search", { includeInactive })).data,
  savePack: async (pack: RechargePackDto): Promise<string> =>
    (await api.post("/Licensing/packs/save", pack)).data,
  removePack: async (id: string) => api.post("/Licensing/packs/remove", { id }),

  getSettings: async (): Promise<LicensingSettingsDto> =>
    (await api.post("/Licensing/settings/get", {})).data,
  updateSettings: async (settings: LicensingSettingsDto) =>
    api.post("/Licensing/settings/update", settings),
  seedDefaults: async () => api.post("/Licensing/settings/seed", {}),

  // ---- plans ----
  searchPlans: async (
    includeInactive = true,
    onlyPublished = false,
  ): Promise<PlanDto[]> =>
    (
      await api.post("/Licensing/plans/search", {
        includeInactive,
        onlyPublished,
      })
    ).data,
  getPlan: async (id: string): Promise<PlanDto> =>
    (await api.post("/Licensing/plans/get", { id })).data,
  savePlan: async (plan: PlanDto): Promise<string> =>
    (await api.post("/Licensing/plans/save", plan)).data,
  setPlanEntitlements: async (payload: {
    planId: string;
    entitlements: PlanEntitlementDto[];
    features: PlanFeatureDto[];
    modules: PlanModuleDto[];
  }) => api.post("/Licensing/plans/setentitlements", payload),
  setPlanState: async (payload: {
    id: string;
    isActive?: boolean;
    isPublished?: boolean;
    remove?: boolean;
  }) => api.post("/Licensing/plans/setstate", payload),
  clonePlan: async (payload: {
    sourcePlanId: string;
    code?: string;
    name?: string;
  }): Promise<string> =>
    (await api.post("/Licensing/plans/clone", payload)).data,

  // ---- organisations' licences (Super Admin) ----
  searchOrganisations: async (payload: {
    organisationIds?: string[];
    searchText?: string;
    planId?: string | null;
    status?: number | null;
    onlyWithPendingRequests?: boolean;
    onlyUnsubscribed?: boolean;
    /** Organisations created on or after / on or before (YYYY-MM-DD). */
    startDate?: string;
    endDate?: string;
    /** name (default), code, newest (created), modificationdate or active. */
    orderBy?: string;
    isAscendingOrder?: boolean;
    pageIndex: number;
    pageSize: number;
  }): Promise<Paged<OrganisationLicenceRowDto>> =>
    (await api.post("/Licensing/organisations/search", payload)).data,
  getOrganisation: async (
    organisationId: string,
  ): Promise<OrganisationLicenceDetailResponse> =>
    (await api.post("/Licensing/organisations/get", { organisationId })).data,
  assignPlan: async (payload: {
    organisationId: string;
    planId: string;
    billingCycle: number;
    startDate?: string | null;
    negotiatedPrice?: number | null;
    notes?: string;
    keepOverrides: boolean;
    autoRenew: boolean;
    discountPercent?: number;
    discountNote?: string;
  }): Promise<string> =>
    (await api.post("/Licensing/subscriptions/assign", payload)).data,
  setSubscriptionStatus: async (payload: {
    subscriptionId: string;
    status: number;
    reason?: string;
  }) => api.post("/Licensing/subscriptions/setstatus", payload),
  extendSubscription: async (payload: {
    subscriptionId: string;
    newPeriodEnd: string;
    autoRenew?: boolean | null;
    notes?: string;
  }) => api.post("/Licensing/subscriptions/extend", payload),
  setOverrides: async (payload: {
    subscriptionId: string;
    overrides: SubscriptionOverrideDto[];
  }) => api.post("/Licensing/subscriptions/setoverrides", payload),
  resolvePlanChange: async (payload: {
    subscriptionId: string;
    approve: boolean;
    notes?: string;
  }) => api.post("/Licensing/subscriptions/resolveplanchange", payload),
  grantRecharge: async (payload: {
    organisationId: string;
    packId?: string | null;
    packType?: number;
    meterKey?: string | null;
    moduleKey?: string | null;
    quantity?: number | null;
    price?: number | null;
    source?: number;
    activateNow: boolean;
    expiresAt?: string | null;
    paymentReference?: string;
    notes?: string;
    discountPercent?: number;
    discountNote?: string;
  }): Promise<string> =>
    (await api.post("/Licensing/recharges/grant", payload)).data,
  activateRecharge: async (payload: {
    rechargeId: string;
    paymentReference?: string;
    notes?: string;
  }) => api.post("/Licensing/recharges/activate", payload),
  cancelRecharge: async (payload: { rechargeId: string; notes?: string }) =>
    api.post("/Licensing/recharges/cancel", payload),
  searchRecharges: async (payload: {
    organisationIds?: string[];
    status?: number | null;
    meterKey?: string | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<Paged<RechargeDto>> =>
    (await api.post("/Licensing/recharges/search", payload)).data,
  adjustUsage: async (payload: {
    organisationId: string;
    meterKey: string;
    used: number;
    note?: string;
  }) => api.post("/Licensing/usage/adjust", payload),

  // ---- my organisation (Org Admin) ----
  mySubscription: async (): Promise<MySubscriptionResponse> =>
    (await api.post("/Licensing/my/subscription", {})).data,
  myLedger: async (payload: {
    meterKey?: string | null;
    userId?: string | null;
    from?: string | null;
    to?: string | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<Paged<LedgerRowDto>> =>
    (await api.post("/Licensing/my/ledger", payload)).data,
  myDailyUsage: async (days = 30): Promise<DailyUsagePoint[]> =>
    (await api.post("/Licensing/my/dailyusage", { days })).data,
  myPacks: async (): Promise<RechargePackDto[]> =>
    (await api.post("/Licensing/my/packs", {})).data,
  purchase: async (payload: {
    packId: string;
    packs: number;
    note?: string;
  }): Promise<string> =>
    (await api.post("/Licensing/my/purchase", payload)).data,
  /** A member without rights over the subscription asks the admins to
   *  upgrade it; they get an email and an in-app notification naming the
   *  member, the organisation and the module wanted. */
  requestUpgrade: async (payload: {
    moduleKey?: string;
    moduleName?: string;
  }) => (await api.post("/notification/requestupgrade", payload)).data,
  requestPlanChange: async (payload: {
    planId?: string | null;
    billingCycle?: number;
    note?: string;
    cancel?: boolean;
  }) => api.post("/Licensing/my/requestplanchange", payload),
  acknowledgeAlert: async (id: string) =>
    api.post("/Licensing/my/acknowledgealert", { id }),

  // ---- billing ----
  myInvoices: async (payload: {
    status?: number | null;
    type?: number | null;
    from?: string | null;
    to?: string | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<Paged<InvoiceDto>> =>
    (await api.post("/Licensing/my/invoices", payload)).data,
  myInvoice: async (id: string): Promise<InvoiceDto> =>
    (await api.post("/Licensing/my/invoices/get", { id })).data,
  myInvoiceEmail: async (payload: {
    invoiceId: string;
    toEmail?: string;
    pdfBase64?: string;
    fileName?: string;
    message?: string;
  }) => api.post("/Licensing/my/invoices/email", payload),
  myPayments: async (payload: {
    invoiceId?: string | null;
    from?: string | null;
    to?: string | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<Paged<PaymentDto>> =>
    (await api.post("/Licensing/my/payments", payload)).data,
  myBillingSummary: async (): Promise<BillingSummaryDto> =>
    (await api.post("/Licensing/my/billingsummary", {})).data,

  searchInvoices: async (payload: {
    organisationIds?: string[];
    status?: number | null;
    type?: number | null;
    from?: string | null;
    to?: string | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<Paged<InvoiceDto>> =>
    (await api.post("/Licensing/invoices/search", payload)).data,
  getInvoice: async (id: string): Promise<InvoiceDto> =>
    (await api.post("/Licensing/invoices/get", { id })).data,
  recordPayment: async (payload: {
    invoiceId: string;
    amount?: number | null;
    method: number;
    reference?: string;
    paidAt?: string | null;
    notes?: string;
  }): Promise<string> =>
    (await api.post("/Licensing/invoices/recordpayment", payload)).data,
  issueInvoice: async (organisationId: string): Promise<string | null> =>
    (await api.post("/Licensing/invoices/issue", { organisationId })).data,
  setInvoiceStatus: async (payload: {
    invoiceId: string;
    status: number;
    notes?: string;
  }) => api.post("/Licensing/invoices/setstatus", payload),
  searchAuditLog: async (
    payload: AuditSearchMessage,
  ): Promise<Paged<AuditLogDto>> =>
    (await api.post("/Licensing/audit/search", payload)).data,
  applyInvoiceDiscount: async (payload: {
    invoiceId: string;
    discountPercent?: number | null;
    discountAmount?: number | null;
    note?: string;
  }) => api.post("/Licensing/invoices/applydiscount", payload),
  emailInvoice: async (payload: {
    invoiceId: string;
    toEmail?: string;
    pdfBase64?: string;
    fileName?: string;
    message?: string;
  }) => api.post("/Licensing/invoices/email", payload),
  searchPayments: async (payload: {
    organisationIds?: string[];
    invoiceId?: string | null;
    from?: string | null;
    to?: string | null;
    pageIndex: number;
    pageSize: number;
  }): Promise<Paged<PaymentDto>> =>
    (await api.post("/Licensing/payments/search", payload)).data,

  // ---- any signed-in user ----
  snapshot: async (): Promise<EntitlementSnapshotResponse> =>
    (await api.post("/Licensing/entitlements/snapshot", {})).data,
  check: async (
    meterKey: string,
    quantity = 1,
  ): Promise<EntitlementCheckResult> =>
    (await api.post("/Licensing/entitlements/check", { meterKey, quantity }))
      .data,
};
